import deepEqual from 'fast-deep-equal'
import uniq from 'lodash/uniq'
import plur from 'plur'
import React, {useRef} from 'react'

import {
  getDraft,
  getPipelineOriginalIntegrations,
} from '../../../../../../EditPipelinePage/EditPipelinePage.selectors'
import {collapsedBlocks} from '../../../../../../EditPipelinePage/slices/EditPipelinePage.slices'
import {usePipelineId} from '../../../../../../hooks/pipeline'
import SettingsBlock from '../../../../SettingsBlock/SettingsBlock'
import {AddIntegration} from '../../Integrations/AddIntegration/AddIntegration'
import Integration from '../../Integrations/Integration/Integration'
import type {IntegrationType} from '../../Integrations/Integrations.consts'
import {integrations} from '../../Integrations/Integrations.consts'
import {useAvailableIntegrations} from '../../Integrations/Integrations.hooks'
import IntegrationSettingsForm from '../../Integrations/IntegrationSettingsForm/IntegrationSettingsForm'

import {useAppDispatch, useAppSelector} from 'src/hooks/react-redux'
import {notNull} from 'src/utils/guards'

function PipelineIntegrations() {
  const availableIntegrations = useAvailableIntegrations()
  const pipelineId = usePipelineId()
  const integrationsas = useAppSelector(state => getDraft(state, pipelineId)?.integrations) ?? []
  const savedIntegrations = useAppSelector(getPipelineOriginalIntegrations(pipelineId)) ?? []
  const count = availableIntegrations.filter(({isDeleted}) => !isDeleted).length

  const dispatch = useAppDispatch()

  const settingsBlockId = 'pipeline-integrations'
  const settingsBlockTitle = count ? plur('Integration', count) : 'No Integrations'

  const [selectedIntegration, setSelectedIntegration] = React.useState<IntegrationType | null>(null)

  const addIntegrationButtonRef = useRef<HTMLInputElement>(null)

  return (
    <>
      <SettingsBlock
        id={settingsBlockId}
        collapsible={availableIntegrations.length > 0 || selectedIntegration != null}
        collapsedDefault
        collapsedNote={uniq(
          availableIntegrations
            .filter(({isDeleted}) => !isDeleted)
            .map(({type}) => integrations[type]?.displayName)
            .filter(notNull),
        ).join(', ')}
        title={settingsBlockTitle}
        onAddClick={() => addIntegrationButtonRef?.current?.click()}
        customActions={anchor => (
          <AddIntegration
            anchor={<div ref={addIntegrationButtonRef}>{anchor}</div>}
            onSelectIntegration={type => {
              setSelectedIntegration(type)
              dispatch(
                collapsedBlocks.actions.toggle({
                  id: settingsBlockId,
                  collapsed: false,
                }),
              )
            }}
          />
        )}
      >
        {availableIntegrations.map((integration, index) => {
          const {type, parameters, isDeleted, id} = integration
          const isUnsaved =
            !isDeleted && !deepEqual(savedIntegrations[index], integrationsas[index])
          const {displayName} = parameters
          const integrationKey = `${displayName}_${type}`

          return (
            <Integration
              id={id!}
              key={integrationKey}
              type={type}
              unsaved={isUnsaved}
              parameters={parameters}
              isDeleted={isDeleted}
            />
          )
        })}
        {selectedIntegration && (
          <IntegrationSettingsForm
            type={selectedIntegration}
            onClose={() => setSelectedIntegration(null)}
          />
        )}
      </SettingsBlock>
    </>
  )
}

export default React.memo(PipelineIntegrations)
